import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

// Seletor de elementos focáveis dentro do modal. Exclui disabled / tabindex=-1
// para garantir que Tab/Shift+Tab só pule entre elementos realmente interativos.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

export interface ModalProps {
  open: boolean
  onClose: () => void
  /**
   * `id` único do elemento de título do consumer, gerado via React `useId()`.
   * Usado pelo `aria-labelledby` para associar o diálogo ao título visível.
   * (O consumer já precisa de `useId` para o título, então pedimos o id
   * pronto — evita Provider + useContext + ordem-de-render frágil.)
   */
  titleId: string
  /** Fallback para `aria-label` do diálogo. Usado por leitores de tela. */
  title: string
  children: React.ReactNode
  /**
   * Quando false, clicks no overlay NÃO fecham o modal. Útil durante requests
   * (submitting) onde fechar sem querer é pior UX. Default: true.
   */
  closeOnOverlay?: boolean
}

// Wrapper acessível para os modais da app. Centraliza:
//   - role="dialog" + aria-modal="true" + aria-labelledby
//   - Escape fecha o modal
//   - Focus trap: Tab/Shift+Tab ciclam dentro do modal
//   - Return focus: ao fechar, devolve foco ao elemento que abriu o modal
//   - Scroll lock no body enquanto o modal está aberto
//   - Portal no document.body (consistente com o padrão do print fix)
//
// Mantém o visual existente (`fz-overlay` / `fz-modal`) — só padroniza o
// wrapper e os comportamentos de a11y. Os modais consumidores preservam
// sua estrutura interna (form, botões, FieldRenderer etc.).
export function Modal({ open, onClose, title, titleId, children, closeOnOverlay = true }: ModalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  const [mounted, setMounted] = useState(false)
  // Audit modais #1: onClose é inline no App (identidade nova a cada render) e o
  // TickProvider re-renderiza o dashboard a cada 15s — com onClose no deps, o effect
  // re-rodava (foco escapava do modal e prev anterior ficava gravado). Guardamos em ref.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  // Marca a transição fechado->aberto: o target do return-focus só é capturado UMA vez,
  // nunca com o foco já dentro do modal (era o bug de foco cair no body).
  const wasOpenRef = useRef(false)

  // Portal exige `document.body` no client. Em SSR / build time não há body.
  // Marcamos `mounted` no client para evitar hidratação mismatch. setState
  // dentro de useEffect dispara re-render síncrono em testes envoltos por
  // act(), então containerRef já existe quando o effect principal roda.
  useEffect(() => { setMounted(true) }, [])

  useEffect(() => {
    if (!open || !mounted) {return}
    if (!wasOpenRef.current) {
      // Save return-focus target (o elemento focado antes do modal abrir).
      // Pode ser null no mount inicial do app — daí guardamos só se existir.
      previouslyFocusedRef.current = (document.activeElement as HTMLElement | null) ?? null
      wasOpenRef.current = true
    }

    // Move foco para o primeiro focável (ou o próprio container como fallback).
    const focusFirst = () => {
      const root = containerRef.current
      if (!root) {return}
      const focusables = root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      const first = focusables[0]
      if (first) {
        first.focus()
      } else {
        root.tabIndex = -1
        root.focus()
      }
    }
    // Chama imediato + rAF: garante foco em jsdom (que NÃO dispara rAF) e
    // cobre prod onde o container pode não estar layoutado no momento do
    // efeito. Idempotente (chamar .focus() duas vezes no mesmo elemento é no-op).
    focusFirst()
    const raf = requestAnimationFrame(focusFirst)

    // Scroll lock — enquanto o modal está aberto, a página de fundo não
    // pode rolar. Restauramos o valor original no cleanup para não pisar
    // em estilos intencionalmente aplicados por outros componentes.
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onCloseRef.current()
        return
      }
      if (e.key !== 'Tab') {return}
      // Focus trap: ao chegar no último focável, Tab volta pro primeiro;
      // Shift+Tab no primeiro vai pro último.
      const root = containerRef.current
      if (!root) {return}
      const focusables = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        .filter((el) => !el.hasAttribute('disabled'))
      if (focusables.length === 0) {
        e.preventDefault()
        return
      }
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement as HTMLElement | null
      const isInModal = active && root.contains(active)
      if (e.shiftKey) {
        // Se está no primeiro OU fora do modal, manda pro último.
        if (active === first || !isInModal) {
          e.preventDefault()
          last.focus()
        }
      } else {
        // Se está no último OU fora do modal, manda pro primeiro.
        if (active === last || !isInModal) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    window.addEventListener('keydown', onKey)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
      // Return focus: devolve foco ao elemento que abriu o modal. Só
      // chamamos se ele ainda estiver no DOM (pode ter sido desmontado).
      const prev = previouslyFocusedRef.current
      if (prev && typeof prev.focus === 'function' && document.contains(prev)) {
        prev.focus()
      }
    }
  }, [open, mounted])

  if (!open || !mounted) {return null}

  const handleOverlayClick = () => {
    if (closeOnOverlay) {onCloseRef.current()}
  }

  return createPortal(
    <div
      ref={containerRef}
      className="fz-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-label={title}
      onClick={handleOverlayClick}
    >
      <div className="fz-modal" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>,
    document.body,
  )
}